import type { MaybeRefOrGetter } from 'vue';
import type { M2MStructure, QuerySchemaRelationWrite } from '#layers/data-model/public';
import type { RelationPermissionsReturn } from '#layers/forms/types/permissions';
import type { M2MEditState } from '#layers/forms/utils/form-write-context';
import { computed, toValue } from 'vue';
import { getRelationPrimitiveFields, queryCapabilityAllows, UNSUPPORTED_CAPABILITY, useQueryApi } from '#layers/data-model/public';
import { FormWriteContext, RelationEditKinds } from '#layers/forms/utils/form-write-context';
import { useRelationPermissionsBase } from './use-relation-permissions-base';

export interface UseM2MPermissionsOptions {
	m2mStructure: MaybeRefOrGetter<M2MStructure | null>;
	/** The parent form's write; junction rows ride its payload. */
	writeContext: MaybeRefOrGetter<FormWriteContext | undefined>;
}

/**
 * Every junction operation rides the parent payload's source relation field: connecting creates a
 * junction row that `_connect`s the target, creating additionally `_create`s the target, and
 * disconnecting `_delete`s the junction row.
 */
export function useM2MPermissions(options: UseM2MPermissionsOptions): RelationPermissionsReturn<M2MEditState> {
	const structure = computed(() => toValue(options.m2mStructure));
	const {
		permissionsStore,
		oppositeColId: junctionColId,
		oppositeConstrainedFieldIds: junctionSourceFkIds,
		writeContext,
		relationAllows: sourceRelationAllows,
	} = useRelationPermissionsBase({
		field: () => structure.value?.source.field ?? null,
		writeContext: options.writeContext,
	});

	const toTarget = computed(() => structure.value?.junction.toTarget);
	const target = computed(() => toTarget.value?.oppositeCollection);
	const targetApi = useQueryApi(target).api;

	const junctionFkIds = computed(() => [
		...junctionSourceFkIds.value,
		...(toTarget.value ? getRelationPrimitiveFields(toTarget.value).fields.map((field) => field.id) : []),
	]);

	const junctionCreate = computed(() => structure.value && writeContext.value?.createRelated(structure.value.source.field));

	function targetRelationAllows(predicate: (relation: QuerySchemaRelationWrite) => boolean): boolean {
		const relation = junctionCreate.value && toTarget.value ? junctionCreate.value.relation(toTarget.value) : UNSUPPORTED_CAPABILITY;
		return queryCapabilityAllows(relation, predicate);
	}

	const canCreateJunction = computed(() => !!junctionColId.value
		&& junctionFkIds.value.length > 0
		&& permissionsStore.canCreate(junctionColId.value)
		&& permissionsStore.canCreateFields(junctionColId.value, junctionFkIds.value));

	const canConnect = computed(() => !!target.value
		&& canCreateJunction.value
		&& targetRelationAllows((relation) => relation.connect())
		&& permissionsStore.canRead(target.value.id));

	const canCreate = computed(() => !!target.value
		&& canCreateJunction.value
		&& targetRelationAllows((relation) => relation.create() !== undefined)
		&& permissionsStore.canCreate(target.value.id));

	const canDisconnect = computed(() => !!junctionColId.value
		&& sourceRelationAllows((relation) => relation.delete())
		&& permissionsStore.canDelete(junctionColId.value));

	const createContext = computed(() => canCreate.value && toTarget.value
		? junctionCreate.value?.createRelated(toTarget.value)
		: undefined);

	function editContext(state: M2MEditState): FormWriteContext | undefined {
		if (state.kind === RelationEditKinds.Created) return createContext.value;
		if (!structure.value || !target.value || !permissionsStore.canUpdate(target.value.id)) return;

		switch (state.kind) {
			case RelationEditKinds.Connected: {
				// The junction only `_connect`s this target; its edits are a separate by-key update after save.
				const context = FormWriteContext.root(target.value.id, targetApi.value, state.key);
				return context.available ? context : undefined;
			}
			case RelationEditKinds.Persisted:
				return writeContext.value
					?.updateRelated(structure.value.source.field, state.junctionKey)
					?.updateRelated(structure.value.junction.toTarget, state.key);
			default:
				state satisfies never;
		}
	}

	return { canConnect, canCreate, canDisconnect, createContext, editContext };
}
